import React from 'react';
import { Button, TrevorIcon } from '@trevor/ui-components';
import { logout } from '@/actions';
import AjaxSpinner from '@/components/AjaxSpinner';
import {
  useCreateSampleDatabaseMutation,
  useGetDatasourcesQuery,
} from '@/features/datasources/apiSlice';
import { userAction } from '@/util/helpers/ajax-helpers';
import {
  Body,
  BodyFooter,
  Header,
} from '@/containers/DatasourcesPage/DatasourcesPage.styles';
import { TestimonialList } from '@/containers/DatasourcesPage/components/TestimonialList';
import { HelpChatWithUs } from '@/containers/DatasourcesPage/components/HelpChatWithUs';
import { ConnectDatasourceSection } from '@/containers/DatasourcesPage/components/ConnectDatasourceSection';
import { TitleAndSubtitleSection } from '@/containers/DatasourcesPage/components/TitleAndSubtitleSection';
import { ConnectionOptionSection } from '@/containers/DatasourcesPage/components/ConnectionOptionSection';
import { DatasourceListSection } from '@/containers/DatasourcesPage/components/DatasourceListSection';
import { useAppDispatch } from '@/store/hooks';

export const DatasourcesPage = () => {
  const dispatch = useAppDispatch();
  const { data, isFetching } = useGetDatasourcesQuery();
  const [createDatasource, { data: createSampleDatasourceData }] =
    useCreateSampleDatabaseMutation();
  const [showConnectDatasourceFlow, setShowConnectDatasourceFlow] =
    React.useState(false);
  const connectDatasourceFlowContainerRef = React.useRef(null);

  const hasDatasources = Boolean(data?.datasources.length);
  const sampleDatasourceId = createSampleDatasourceData?.datasourceId;

  React.useEffect(() => {
    if (!sampleDatasourceId) return;
    userAction('sample-datasource-created', {
      datasourceId: sampleDatasourceId,
    });
  }, [sampleDatasourceId]);

  const handleConnectSampleDatasource = () => {
    createDatasource();
    setShowConnectDatasourceFlow(false);
  };

  const handleShowConnectDatasourceFlow = () => {
    userAction('connect-database-start-clicked');
    setShowConnectDatasourceFlow(true);
  };

  const displaySection = () => {
    if (hasDatasources) {
      return (
        <DatasourceListSection
          email={data?.email}
          numberOfDatasources={data?.datasources.length}
          showConnectButton={
            !showConnectDatasourceFlow &&
            (!data?.selfHosted || data.isSuperAdmin)
          }
          onConnectDatabase={handleShowConnectDatasourceFlow}
        />
      );
    }

    return (
      <ConnectionOptionSection
        isLoading={isFetching}
        isSelfHosted={Boolean(data?.selfHosted)}
        isSuperAdmin={Boolean(data?.isSuperAdmin)}
        showConnectDatabaseButton={!showConnectDatasourceFlow}
        onConnectDatabase={handleShowConnectDatasourceFlow}
      />
    );
  };

  React.useEffect(() => {
    if (showConnectDatasourceFlow && connectDatasourceFlowContainerRef) {
      // @ts-expect-error: Object is possibly 'null'.
      connectDatasourceFlowContainerRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [showConnectDatasourceFlow]);

  return (
    <>
      <Header>
        <TrevorIcon name="logo-wordmark" height="20" width="116" />
        <Button variant="secondary" onClick={() => dispatch(logout())}>
          Logout
        </Button>
      </Header>
      <Body>
        <TitleAndSubtitleSection showSubtitle={!hasDatasources} />
        {isFetching ? <AjaxSpinner /> : displaySection()}
        <div ref={connectDatasourceFlowContainerRef}>
          {showConnectDatasourceFlow && (
            <ConnectDatasourceSection
              onConnectSampleDatabase={handleConnectSampleDatasource}
              onClose={() => setShowConnectDatasourceFlow(false)}
            />
          )}
        </div>
        <BodyFooter>
          <HelpChatWithUs />
          <TestimonialList />
        </BodyFooter>
      </Body>
    </>
  );
};
